/**
 * Unified-diff tokenizer for the PR review viewer.
 *
 * Pure, no React. Tokenizes a FileDiff into display lines:
 *   - 'add'    → "+<text>" (new-side line numbers increment)
 *   - 'del'    → "-<text>" (new-side line numbers do NOT increment; num = 0)
 *   - 'context'→ " <text>" (new-side line numbers increment)
 *
 * Falls back to plain content (all context, numbered from 1) when `diff` is empty
 * — currently every PR in the DB has diff === '' because the worker hasn't captured
 * a unified diff yet. Iteration 2 will populate `diff` from `gh pr diff`.
 *
 * Run the self-check: `npx tsx web/src/lib/diff.ts`.
 */

export type LineType = 'add' | 'del' | 'context';
export interface DisplayLine {
  /** New-side line number. 0 for pure deletions (no anchor in the proposed file). */
  num: number;
  type: LineType;
  text: string;
}

const HUNK_RE = /^@@ -\d+(?:,\d+)? \+(\d+)(?:,\d+)? @@/;

export interface FileLike {
  content: string;
  diff: string;
}

export function tokenizeFile(file: FileLike): DisplayLine[] {
  if (!file.diff || !file.diff.trim()) {
    return file.content.split('\n').map((text, i) => ({
      num: i + 1,
      type: 'context' as const,
      text,
    }));
  }
  const out: DisplayLine[] = [];
  let newLine = 0;
  for (const raw of file.diff.split('\n')) {
    if (raw.startsWith('@@')) {
      const m = HUNK_RE.exec(raw);
      if (m) newLine = parseInt(m[1], 10) - 1;
      continue;
    }
    // Skip diff file headers ("+++ b/path", "--- a/path")
    if (raw.startsWith('+++') || raw.startsWith('---')) continue;
    if (raw === '') {
      out.push({ num: ++newLine, type: 'context', text: '' });
      continue;
    }
    const marker = raw[0];
    const text = raw.slice(1);
    if (marker === '+') out.push({ num: ++newLine, type: 'add', text });
    else if (marker === '-') out.push({ num: 0, type: 'del', text });
    else if (marker === ' ') out.push({ num: ++newLine, type: 'context', text });
    // Unknown marker (e.g. "\ No newline"): ignore rather than render as context.
  }
  return out;
}

/** Lines that have a review comment pinned to them, by new-side line number. */
export function commentLinesFor(
  lines: DisplayLine[],
  comments: ReadonlyArray<{ line: number; filepath?: string }>,
  activeFilepath: string,
): Set<number> {
  const matches = new Set<number>();
  for (const c of comments) {
    if (c.filepath && c.filepath !== activeFilepath) continue;
    if (c.line <= 0) continue;
    matches.add(c.line);
  }
  // Keep only line numbers that actually exist in the file (sanity).
  const present = new Set(lines.map((l) => l.num).filter((n) => n > 0));
  return new Set([...matches].filter((n) => present.has(n)));
}

// ---- Self-check (runs only when this file is invoked directly via tsx) ----
// Guard `process` — it's a Node global; evaluating it in the browser throws
// before the condition can short-circuit. `import.meta.url` resolves to a
// `file://` path under tsx, never in the Vite browser bundle.
if (typeof process !== 'undefined' && import.meta.url === `file://${process.argv[1]}`) {
  // Wrapped in an async IIFE: top-level await is invalid in vite's browser
  // build target, and this module is bundled once DiffViewer imports it. The
  // IIFE keeps the dynamic import out of module top-level while preserving the
  // `npx tsx web/src/lib/diff.ts` self-check.
  void (async () => {
  // Lazy-require so the module is import-clean elsewhere.
  const assert: typeof import('node:assert/strict') = (await import('node:assert/strict')).default;

  // 1) Empty diff → context lines numbered 1..N
  const plain = tokenizeFile({ content: 'a\nb\nc', diff: '' });
  assert.equal(plain.length, 3);
  assert.equal(plain[0].num, 1);
  assert.equal(plain[0].type, 'context');
  assert.equal(plain[0].text, 'a');
  assert.equal(plain[2].text, 'c');

  // 2) Unified diff with add, del, context, hunk header, file header
  const diff = [
    'diff --git a/x.ts b/x.ts',
    '--- a/x.ts',
    '+++ b/x.ts',
    '@@ -1,3 +1,4 @@',
    ' line1',
    '-line2',
    '+line2-modified',
    '+line2b-new',
    ' line3',
  ].join('\n');
  const toks = tokenizeFile({ content: 'line1\nline2\nline3', diff });
  // Filter out file-header lines (handled in tokenize: skipped)
  assert.equal(toks.length, 5, `expected 5 tokens, got ${toks.length}`);
  assert.deepEqual(
    toks.map((t) => ({ n: t.num, k: t.type })),
    [
      { n: 1, k: 'context' },
      { n: 0, k: 'del' },
      { n: 2, k: 'add' },
      { n: 3, k: 'add' },
      { n: 4, k: 'context' },
    ],
  );
  assert.equal(toks[0].text, 'line1');
  assert.equal(toks[1].text, 'line2');
  assert.equal(toks[2].text, 'line2-modified');
  assert.equal(toks[3].text, 'line2b-new');
  assert.equal(toks[4].text, 'line3');

  // 3) Hunk header sets starting newLine (e.g. @@ -40 +100 @@ → newLine 99 before first)
  const diff2 = ['@@ -40 +100 @@', ' alpha', '+ beta'].join('\n');
  const toks2 = tokenizeFile({ content: '', diff: diff2 });
  assert.equal(toks2[0].num, 100);
  assert.equal(toks2[1].num, 101);

  // 4) commentLinesFor: filepath-filter + sanity
  const cs = commentLinesFor(
    [{ num: 100, type: 'context', text: 'a' }, { num: 5, type: 'context', text: 'b' }],
    [
      { line: 100, filepath: 'src/x.ts' },
      { line: 5, filepath: 'src/y.ts' }, // different file → excluded
      { line: 5 }, // no filepath → included
    ],
    'src/x.ts',
  );
  assert.deepEqual([...cs].sort((a, b) => a - b), [5, 100]);

  // eslint-disable-next-line no-console
  console.log('diff.ts self-check: OK');
  })().catch((e) => { console.error(e); process.exit(1); });
}
